import type {
  GraphMember,
  TreeRelationshipWorkflow,
} from '@/contexts/FamilyGraphContext';

import type {
  CanonicalTreeEdgeKind,
  CanonicalTreeEdgeStatus,
  CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

import {
  expectedCanonicalTreeEdgeDirection,
  validateCanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdgeAuthority';

const workflowKindToEdgeKind = (
  member: GraphMember,
  workflow: TreeRelationshipWorkflow,
  memberById: ReadonlyMap<string, GraphMember>,
): CanonicalTreeEdgeKind | null => {
  switch (workflow.kind) {
    case 'former-spouse-partner':
    case 'spouse-partner':
      return 'spouse-partner';

    case 'co-parent':
      /*
       * CC-TREE-LIVE-003G3WJ2
       *
       * Co-parent truth is child-mediated and multiplicity-aware. A stored
       * workflow preserves authoring intent, but canonical co-parent edges are
       * derived from the intersection of adult parent-child facts so one adult
       * pair can carry every shared child without duplicate identities.
       */
      return null;

    case 'step-family':
      return 'step-family';

    case 'in-law':
      return 'in-law';

    case 'guardian':
      return 'guardian';

    case 'foster-family':
      return 'foster-family';

    case 'god-family':
      return 'god-family';

    case 'chosen-family':
      return 'chosen-family';

    case 'affiliated-family':
      return 'affiliated-family';

    case 'structural':
      /*
       * Explicit lateral relationship authority.
       *
       * A stored sibling, aunt/uncle, great-aunt/uncle, or deeper lateral
       * relative with a workflow connector represents "sibling of connector".
       *
       * This remains valid even when the connected siblings' parents are
       * unknown. No parent IDs are manufactured.
       */
      const connector = workflow.connectorMemberId
        ? memberById.get(workflow.connectorMemberId)
        : null;

      /*
       * Explicit SIBLING_OF is workflow authority, not label authority.
       *
       * A structural workflow with a valid endpoint and createsAncestry=false
       * is lateral regardless of the GraphMember compatibility relationship.
       * Existing parent evidence on either endpoint remains valid.
       */
      const sameOwner =
        connector !== null &&
        typeof member.ownerUserId === 'string' &&
        member.ownerUserId.length > 0 &&
        member.ownerUserId ===
          connector.ownerUserId;

      const explicitLateralWorkflow =
        sameOwner &&
        workflow.createsAncestry === false &&
        workflow.inheritsGeneration === true &&
        workflow.inheritsBranch === true;

      const historicalAuntUncleLateralWorkflow =
        sameOwner &&
        workflow.createsAncestry === true &&
        member.relationship === 'aunt-uncle' &&
        (member.parentIds ?? [])
          .filter(Boolean)
          .length === 0 &&
        workflow.inheritsGeneration === true &&
        workflow.inheritsBranch === true;

      if (
        explicitLateralWorkflow ||
        historicalAuntUncleLateralWorkflow
      ) {
        return 'sibling';
      }

      /*
       * Parent-child ancestry is already represented canonically by
       * parent_ids. Do not create a competing workflow edge for it.
       */
      return null;

    case 'display-only':
    default:
      return null;
  }
};

const workflowStatus = (
  workflow: TreeRelationshipWorkflow,
): CanonicalTreeEdgeStatus => {
  switch (workflow.partnershipStatus) {
    case 'current':
      return 'active';

    case 'former':
      return 'former';

    case 'coparent-only':
      return 'active';

    case 'not-applicable':
    case undefined:
    default:
      return 'not-applicable';
  }
};

const workflowSharedChildIds = (
  workflow: TreeRelationshipWorkflow,
  kind: CanonicalTreeEdgeKind,
): readonly string[] => {
  /*
   * WJ2: only legacy direct in-law workflow evidence remains child-mediated.
   * Affiliated Family is a direct non-ancestral affiliation anchor and must
   * not reinterpret its connector as a child.
   */
  if (kind !== 'in-law') {
    return [];
  }

  const ids = [
    workflow.connectorMemberId,
    workflow.secondaryConnectorMemberId,
  ].filter((value): value is string => Boolean(value));

  return Array.from(new Set(ids));
};

const workflowEndpointId = (
  workflow: TreeRelationshipWorkflow,
  kind: CanonicalTreeEdgeKind,
): string | null => {
  /*
   * Legacy direct in-law workflow evidence uses connectorMemberId as
   * mediated evidence and secondaryConnectorMemberId as the adult endpoint.
   * Affiliated Family uses connectorMemberId directly as its real anchor.
   * Co-parent is not handled here: WJ2 derives it from shared parentage.
   */
  if (kind === 'in-law') {
    return workflow.secondaryConnectorMemberId ?? null;
  }

  return workflow.connectorMemberId ?? null;
};

export interface CanonicalWorkflowAdapterResult {
  readonly edges: readonly CanonicalTreeRelationshipEdge[];
  readonly skippedMemberIds: readonly string[];
  readonly deferredMemberIds: readonly string[];
  readonly unresolvedMemberIds: readonly string[];
  readonly invalidMemberIds: readonly string[];
}

export const adaptRelationshipWorkflowsToCanonicalEdges = (
  members: readonly GraphMember[],
): CanonicalWorkflowAdapterResult => {
  const memberById = new Map(
    members.map(member => [member.id, member] as const),
  );
  const memberIds = new Set(memberById.keys());
  const edges: CanonicalTreeRelationshipEdge[] = [];
  const skippedMemberIds: string[] = [];
  const deferredMemberIds: string[] = [];
  const unresolvedMemberIds: string[] = [];
  const invalidMemberIds: string[] = [];

  for (const member of members) {
    const workflow = member.relationshipWorkflow;

    if (!workflow) continue;

    const kind = workflowKindToEdgeKind(
      member,
      workflow,
      memberById,
    );

    if (!kind) {
      skippedMemberIds.push(member.id);

      const unresolvedStructuralConnector =
        workflow.kind === 'structural' &&
        member.relationship === 'aunt-uncle' &&
        (
          !workflow.connectorMemberId ||
          !memberIds.has(workflow.connectorMemberId)
        );

      if (unresolvedStructuralConnector) {
        unresolvedMemberIds.push(member.id);
      } else {
        deferredMemberIds.push(member.id);
      }

      continue;
    }

    const targetMemberId = workflowEndpointId(workflow, kind);

    if (
      !targetMemberId ||
      !memberIds.has(targetMemberId) ||
      targetMemberId === member.id
    ) {
      skippedMemberIds.push(member.id);
      unresolvedMemberIds.push(member.id);
      continue;
    }

    const status = workflowStatus(workflow);
    const sharedChildIds =
      workflowSharedChildIds(workflow, kind);

    const edge: CanonicalTreeRelationshipEdge = {
      id: `workflow:${member.id}:${targetMemberId}:${kind}`,
      ownerUserId: member.ownerUserId ?? null,

      sourceMemberId: member.id,
      targetMemberId,
      kind,
      direction: expectedCanonicalTreeEdgeDirection(kind),
      status,

      sharedChildIds,

      createsAncestry:
        kind === 'parent-child'
          ? workflow.createsAncestry
          : false,

      inheritsGeneration:
        workflow.inheritsGeneration,

      inheritsBranch:
        workflow.inheritsBranch,

      inheritsSide:
        kind === 'sibling'
          ? true
          : workflow.inheritsBranch,

      createsInLawship:
        kind === 'in-law',

      rendersActivePartnership:
        kind === 'spouse-partner' &&
        status === 'active',

      affiliatedBranch:
        [
          'in-law',
          'guardian',
          'foster-family',
          'chosen-family',
          'god-family',
          'affiliated-family',
        ].includes(kind),

      startedAt: null,
      endedAt: null,

      confirmation:
        member.approvalStatus === 'approved'
          ? 'confirmed'
          : member.approvalStatus === 'rejected'
            ? 'rejected'
            : 'pending',

      visibility: 'family',

      preferredFamilyTitle:
        workflow.preferredFamilyTitle ?? null,

      source: 'relationship_workflow',
      sourceReferenceId: member.id,

      metadata: Object.freeze({
        workflowKind: workflow.kind,
        coreRelationship: member.relationship,
        side: member.side,
        secondaryConnectorMemberId:
          workflow.secondaryConnectorMemberId ?? null,
      }),
    };

    const validation =
      validateCanonicalTreeRelationshipEdge(edge);

    if (!validation.valid) {
      invalidMemberIds.push(member.id);
      continue;
    }

    edges.push(Object.freeze(edge));
  }

  return Object.freeze({
    edges: Object.freeze(edges),
    skippedMemberIds:
      Object.freeze(skippedMemberIds),
    deferredMemberIds:
      Object.freeze(deferredMemberIds),
    unresolvedMemberIds:
      Object.freeze(unresolvedMemberIds),
    invalidMemberIds:
      Object.freeze(invalidMemberIds),
  });
};
